import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Facility } from 'src/app/services/firebase-utilities.service';
import { PatientTransferInfoComponent } from '../patient-transfer-info/patient-transfer-info.component';
import { SharingPatientId } from '../sharing-setup/sharing-setup.component';

type ActionOption = 'images-sending' | 'images-retrieving' | 'documents-sending' | 'documents-retrieving' | 'patient-library';

@Component({
    selector: 'app-patients-actions',
    templateUrl: './patients-actions.component.html',
    styleUrls: ['./patients-actions.component.scss'],
    standalone: false
})
export class PatientsActionsComponent {
  @Input() facilities: Facility[] = [];
  @Input() otherPatientIds: SharingPatientId[] = [];
  @Input() selectedTargetFacility: SharingPatientId | null = null;
  @Input() selectedFacilityUsers: any[] = [];
  @Input() pacsUrl: string | null = null;
  @Input() patientId = '';
  @Input() patientName = '';
  @Input() dateOfBirth = '';
  @Input() gender = '';
  @Input() sourceFacility = '';

  constructor(private dialog: MatDialog) {}

  @Output() decisionSelected = new EventEmitter<'images-sending' | 'images-retrieving' | 'documents-sending' | 'documents-retrieving'>();
  @Output() openClientProfile = new EventEmitter<void>();
  @Output() facilitySelected = new EventEmitter<SharingPatientId>();
  @Output() facilityUsersSelected = new EventEmitter<any[]>();
  @Output() continue = new EventEmitter<'images-sending' | 'images-retrieving' | 'documents-sending' | 'documents-retrieving'>();
  @Output() resetRequested = new EventEmitter<void>();
  @Output() openPacsRequested = new EventEmitter<void>();
  // DEV ONLY — remove before release
  @Output() devClientProfileRequested = new EventEmitter<void>();

  selectedOption: ActionOption | null = null;
  get selectedFacilityDisplayName(): string {
    const facilityId = this.selectedTargetFacility?.facility;
    if (!facilityId) {
      return '';
    }
    const match = (this.facilities || []).find(facility => facility?.id === facilityId);
    return match?.name || facilityId;
  }

  onToggleOption(option: ActionOption, checked: boolean): void {
    if (!checked) {
      if (this.selectedOption === option) {
        this.selectedOption = null;
        this.resetRequested.emit();
      }
      return;
    }

    this.selectedOption = option;

    // Retrieve Images: go directly to studies page — no facility selection needed
    if (option === 'images-retrieving') {
      this.continue.emit('images-retrieving');
      return;
    }

    // All other options need facility selection first
    if (option === 'images-sending' || option === 'documents-sending' || option === 'documents-retrieving') {
      this.decisionSelected.emit(option);
    }
    // For document options, we'll show facilities and wait for continue button before opening clientprofile
  }

  onFacilitySelected(facility: Facility): void {
    if (!facility?.id) {
      return;
    }
    const normalizedId = facility.id.toLowerCase().trim();
    const matchingPatientId =
      this.otherPatientIds?.find(pid => (pid.facility || '').toLowerCase().trim() === normalizedId) || null;

    const selected: SharingPatientId =
      matchingPatientId || {
        facility: facility.id,
        value: '',
        ownerId: ''
      };

    this.facilitySelected.emit(selected);
  }

  onFacilityUsersSelected(users: any[]): void {
    this.facilityUsersSelected.emit(users || []);
  }

  openTransferInfoModal(): void {
    const targetFacility = this.selectedFacilityDisplayName;
    const ref = this.dialog.open(PatientTransferInfoComponent, {
      width: '480px',
      data: {
        patientId: this.patientId,
        patientName: this.patientName,
        dateOfBirth: this.dateOfBirth,
        gender: this.gender,
        sourceFacility: this.sourceFacility,
        targetFacility,
      },
    });
    ref.afterClosed().subscribe(result => {
      if (result === 'continue') {
        this.continue.emit('images-sending');
      }
    });
  }

}
